/**
 * Whether the server answers, beside `navigator.onLine`: in a basement or on a building site the
 * device often believes it is online while no request gets through. After a request failed for
 * lack of network the app counts as offline (`useOnline`) until an answer comes in again; until
 * then it asks `/api/health` every few seconds (docs/architecture/offline.md, Verbindung).
 */

/** How often the app asks whether the server is back. */
export const PROBE_INTERVAL_MS = 15_000;
const PROBE_TIMEOUT_MS = 10_000;

let reachable = true;
let probe: ReturnType<typeof setInterval> | null = null;
let probing: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function isReachable(): boolean {
  return reachable;
}

export function subscribeReachable(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** An answer came from the server, whatever its status. */
export function markReachable(): void {
  if (probe) {
    clearInterval(probe);
    probe = null;
  }
  if (reachable) return;
  reachable = true;
  emit();
}

/** A request failed without an answer: no network, or it took too long. */
export function markUnreachable(): void {
  probe ??= setInterval(() => void probeServer(), PROBE_INTERVAL_MS);
  if (!reachable) return;
  reachable = false;
  emit();
}

/** Asks the server once whether it answers; the API client marks the result. */
export function probeServer(): Promise<void> {
  if (reachable || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    return Promise.resolve();
  }
  probing ??= ask().finally(() => {
    probing = null;
  });
  return probing;
}

async function ask(): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch('/api/health', { cache: 'no-store', signal: controller.signal });
    markReachable();
  } catch {
    // Still no answer: the next probe tries again.
  } finally {
    clearTimeout(timer);
  }
}

/** Back to the start state, for tests. */
export function resetReachability(): void {
  if (probe) clearInterval(probe);
  probe = null;
  reachable = true;
}
